/**
 * Bomet Municipal Business Database Management System
 * Copyright (c) 2024 Bett Kipngetich Nicholas
 * All rights reserved.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function PublicDirectory() {
  const [businesses, setBusinesses] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [subCounty, setSubCounty] = useState('');
  const [ward, setWard] = useState('');
  const [subWard, setSubWard] = useState('');
  const [filters, setFilters] = useState({ sub_counties: [], wards: [], sub_wards: [] });

  // Fetch filter options
  useEffect(() => {
    const params = new URLSearchParams();
    if (subCounty) params.set('sub_county', subCounty);
    if (ward) params.set('ward', ward);
    axios.get(`${API_URL}/api/public/filters/?${params}`).then(r => setFilters(r.data));
  }, [subCounty, ward]);

  // Fetch business data
  const fetchData = useCallback(() => {
    const params = new URLSearchParams({ page });
    if (search) params.set('search', search);
    if (subCounty) params.set('sub_county', subCounty);
    if (ward) params.set('ward', ward);
    if (subWard) params.set('sub_ward', subWard);
    axios.get(`${API_URL}/api/public/directory/?${params}`).then(r => {
      setBusinesses(r.data.results);
      setCount(r.data.count);
    });
  }, [page, search, subCounty, ward, subWard]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.ceil(count / 25);

  return (
    <div className="public-directory">
      {/* Header */}
      <div className="public-header">
        <div className="public-header-content">
          <h1>Bomet Municipal Business Directory</h1>
          <p>Registered businesses across Bomet Municipality sub-counties</p>
        </div>
      </div>

      <div className="public-container">
        {/* Filters */}
        <div className="public-filters">
          <input
            placeholder="Search business name, type..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          <select value={subCounty} onChange={e => { setSubCounty(e.target.value); setWard(''); setSubWard(''); setPage(1); }}>
            <option value="">All Sub Counties</option>
            {filters.sub_counties.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={ward} onChange={e => { setWard(e.target.value); setSubWard(''); setPage(1); }} disabled={!subCounty}>
            <option value="">All Wards</option>
            {filters.wards.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
          <select value={subWard} onChange={e => { setSubWard(e.target.value); setPage(1); }} disabled={!ward}>
            <option value="">All Sub Wards</option>
            {filters.sub_wards.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <p className="public-count">{count.toLocaleString()} business{count !== 1 ? 'es' : ''} found</p>

        {/* Table */}
        <div className="public-table-wrap">
          <table className="public-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Business Name</th>
                <th>Business Code</th>
                <th>Nature of Business</th>
                <th>Contact Address</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {businesses.length === 0 ? (
                <tr><td colSpan="6" style={{textAlign:'center',padding:'40px',color:'#999'}}>No businesses found</td></tr>
              ) : businesses.map((b, i) => (
                <tr key={i}>
                  <td className="row-num">{(page - 1) * 25 + i + 1}</td>
                  <td className="biz-name">{b.business_name}</td>
                  <td>{b.business_code}</td>
                  <td>{b.nature_of_business}</td>
                  <td className="masked-contact">{b.contact_address}</td>
                  <td className="location">{b.location}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="public-pagination">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Previous</button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        )}

        {/* Footer */}
        <div className="public-footer">
          <p>Bomet Municipal Business Database Management System</p>
          <p>Contact information partially masked per Data Protection Act, 2019</p>
          <p style={{marginTop:'8px',fontSize:'11px'}} title="Developed by Bett Nicholas 0724302992">© 2025-2026 Bomet Municipality. All rights reserved.</p>
          <a href="/login" className="admin-link">Staff Login →</a>
        </div>
      </div>
    </div>
  );
}
